import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useGameSocket } from "@/lib/socket";
import { Avatar, PaperButton, Timer, RoleBadge, AudioSettings } from "@/components/paper";
import { DrawingCanvas } from "@/game/DrawingCanvas";
import { audio } from "@/lib/audio";
import { toast } from "sonner";
import {
  Copy, Share2, QrCode, Crown, Check, LogOut, Users, Send, Play, Loader2,
  Skull, ShieldCheck, Trophy, RotateCcw, Home as HomeIcon, DoorOpen,
} from "lucide-react";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export default function GameRoom() {
  const { code } = useParams();
  const nav = useNavigate();
  const { playerId, username, avatar } = useApp();
  const canvasRef = useRef(null);
  const submittedRef = useRef(false);
  const prevPhase = useRef(null);

  useEffect(() => {
    if (!username) nav(`/join/${code}`);
  }, [username, code, nav]);

  const { state, connected, reconnecting, chat, reactions, send, fatalError } =
    useGameSocket({ code: (code || "").toUpperCase(), playerId, name: username, avatar, enabled: !!username });

  const phase = state?.phase;
  const you = state?.you;

  // phase-transition sounds
  useEffect(() => {
    if (!phase || phase === prevPhase.current) return;
    const p = phase;
    if (p === "prompt") audio.play("gameStart");
    else if (p === "drawing") { audio.play("drawStart"); submittedRef.current = false; }
    else if (p === "reveal") audio.play("drawEnd");
    else if (p === "hotseat") { audio.play("hotSeat"); setTimeout(() => audio.play("impact"), 900); }
    else if (p === "defense") audio.play("defenseStart");
    else if (p === "elimination") {
      const r = state.result;
      audio.play("elimination");
      setTimeout(() => {
        if (!r) return;
        if (r.survived) audio.play(you?.role === "INK_IMPOSTER" ? "imposterSurvive" : "survive");
        else if (r.role === "INK_IMPOSTER") audio.play("imposterCaught");
        else if (r.role === "MISDIRECTOR") audio.play("misdirector");
        else audio.play("roleReveal");
      }, 1200);
    } else if (p === "scoring") audio.play("score");
    else if (p === "game_over") { audio.play("winner"); audio.startMusic(); }
    if (prevPhase.current === "game_over" && p !== "game_over") audio.stopMusic();
    prevPhase.current = p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => () => audio.stopMusic(), []);

  const onStroke = (dataUrl) => { if (phase === "drawing") send({ type: "submit_drawing", data: dataUrl }); };
  const submitDone = () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    const img = canvasRef.current?.getDataURL();
    send({ type: "done_drawing", data: img });
    audio.play("voteLock");
  };
  const leave = () => { nav("/"); };

  if (fatalError) {
    return (
      <Centered>
        <div className="card-paper p-8 max-w-md text-center">
          <div className="font-hand font-bold text-3xl mb-2">Oops!</div>
          <p className="font-semibold text-ink2 mb-5">{fatalError}</p>
          <PaperButton color="#FFDD2D" onClick={() => nav("/")}>Back Home</PaperButton>
        </div>
      </Centered>
    );
  }

  if (!state) {
    return (
      <Centered>
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-3" size={40} strokeWidth={2.5} />
          <div className="font-hand font-bold text-2xl">{reconnecting ? "Reconnecting…" : "Joining room…"}</div>
          <div className="font-semibold text-ink2 mt-1">Room {(code || "").toUpperCase()}</div>
        </div>
      </Centered>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b-2 border-ink/20" style={{ background: "#F4EBD0" }}>
        <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="font-hand font-bold text-xl">LOSTDC</span>
            <span className="sticky-note bg-sticky-yellow font-hand font-bold px-2 py-0.5 text-sm hidden sm:inline">Room {state.code}</span>
          </div>
          <div className="flex items-center gap-2">
            {phase !== "lobby" && phase !== "game_over" && (
              <span className="font-hand font-bold text-sm">Round {state.round}/{state.totalRounds}</span>
            )}
            {!connected && <span className="text-xs font-bold text-role-imposter animate-pulse">● offline</span>}
            <AudioSettings />
            <button data-testid="leave-room" onClick={leave} className="btn-sketch p-2" title="Leave"><LogOut size={18} strokeWidth={2.5} /></button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {phase === "lobby" && <Lobby state={state} you={you} send={send} />}
        {phase === "prompt" && <PromptView state={state} you={you} />}
        {(phase === "drawing") && <DrawingView state={state} you={you} canvasRef={canvasRef} onStroke={onStroke} onDone={submitDone} submitted={you?.submitted} />}
        {phase === "reveal" && <RevealView state={state} />}
        {(phase === "inspection" || phase === "first_vote") && <VoteView state={state} you={you} send={send} />}
        {phase === "skip_result" && <SkipView />}
        {phase === "hotseat" && <HotSeatView state={state} />}
        {phase === "defense" && <DefenseView state={state} you={you} send={send} chat={chat} reactions={reactions} />}
        {phase === "final_vote" && <FinalVoteView state={state} you={you} send={send} chat={chat} reactions={reactions} />}
        {(phase === "elimination") && <EliminationView state={state} />}
        {phase === "scoring" && <ScoringView state={state} you={you} />}
        {phase === "game_over" && <GameOverView state={state} you={you} send={send} nav={nav} playerId={playerId} />}
      </main>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex items-center justify-center px-4">{children}</div>;
}

/* ============================= LOBBY ============================= */
const SETTING_OPTS = {
  rounds: { label: "Rounds", opts: [3, 5, 7, 10] },
  imposters: { label: "Imposters", opts: ["AUTO", 1, 2, 3] },
  drawTime: { label: "Draw Time", opts: [20, 30, 45, 60], suffix: "s" },
  inspectTime: { label: "Inspection", opts: [10, 15, 20], suffix: "s" },
  defenseTime: { label: "Defense", opts: [10, 15, 20, 30], suffix: "s" },
  finalVoteTime: { label: "Final Vote", opts: [5, 10, 15], suffix: "s" },
  difficulty: { label: "Difficulty", opts: ["Easy", "Normal", "Hard"] },
};

function Lobby({ state, you, send }) {
  const [showQR, setShowQR] = useState(false);
  const isHost = you?.isHost;
  const joinUrl = `${ORIGIN}/join/${state.code}`;
  const n = state.players.length;
  const readyCount = state.players.filter((p) => p.ready || p.isHost).length;

  const setS = (k, v) => { if (isHost) { audio.play("click"); send({ type: "update_settings", settings: { [k]: v } }); } };

  const copy = async (txt, msg) => { try { await navigator.clipboard.writeText(txt); toast.success(msg); audio.play("success"); } catch { toast.error("Copy failed"); } };
  const share = async () => {
    audio.play("click");
    if (navigator.share) { try { await navigator.share({ title: "LOSTDC Imposter Draw", text: `Join my room ${state.code}!`, url: joinUrl }); } catch {} }
    else copy(joinUrl, "Join link copied!");
  };

  const start = () => {
    if (n < state.minPlayers) { toast.error(`Need at least ${state.minPlayers} players.`); audio.play("error"); return; }
    audio.play("gameStart"); send({ type: "start_game" });
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8 space-y-6">
        <div className="card-paper p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-hand font-bold text-lg text-ink2">ROOM CODE</div>
              <div className="font-hand font-bold text-5xl tracking-[0.2em]" data-testid="lobby-room-code">{state.code}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <PaperButton data-testid="copy-code" className="text-base px-4 py-2.5" onClick={() => copy(state.code, "Room code copied!")}><Copy size={16} strokeWidth={2.5} className="inline mr-1" />Copy</PaperButton>
              <PaperButton data-testid="share-room" className="text-base px-4 py-2.5" onClick={share}><Share2 size={16} strokeWidth={2.5} className="inline mr-1" />Share</PaperButton>
              <PaperButton data-testid="qr-toggle" className="text-base px-4 py-2.5" onClick={() => { audio.play("click"); setShowQR((v) => !v); }}><QrCode size={16} strokeWidth={2.5} className="inline mr-1" />QR</PaperButton>
            </div>
          </div>
          {showQR && (
            <div className="mt-4 flex flex-col items-center animate-pop-in">
              <img alt="Join QR code" className="card-flat p-2 bg-white" width={180} height={180} src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(joinUrl)}`} />
              <div className="font-semibold text-ink2 text-sm mt-2 break-all text-center">{joinUrl}</div>
            </div>
          )}
        </div>

        <div className="card-paper p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-hand font-bold text-2xl flex items-center gap-2"><Users strokeWidth={2.5} /> Players</div>
            <span className="font-hand font-bold sticky-note bg-sticky-blue px-3 py-1">{n}/{state.maxPlayers}</span>
          </div>
          {(n < 5 || n > 8) && (
            <div className="mb-3 font-semibold text-sm sticky-note bg-sticky-orange px-3 py-1.5 inline-block">Recommended: 5–8 players for the best game.</div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {state.players.map((p, i) => (
              <div key={p.id} data-testid={`lobby-player-${p.id}`} className="card-flat p-3 flex items-center gap-3 animate-fade-up" style={{ animationDelay: `${i * 40}ms`, background: p.id === you?.id ? "#FFF7D6" : "#FDFBF7", opacity: p.connected ? 1 : 0.5 }}>
                <Avatar seed={p.avatar} size={44} />
                <div className="min-w-0">
                  <div className="font-hand font-bold truncate flex items-center gap-1">{p.name}{p.isHost && <Crown size={14} className="text-marker-yellow inline" fill="#FFDD2D" strokeWidth={2} />}</div>
                  <div className="text-xs font-bold" style={{ color: p.isHost || p.ready ? "#3E9E42" : "#4A4744" }}>{p.isHost ? "HOST" : p.ready ? "READY" : "not ready"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="lg:col-span-4 space-y-6">
        <div className="card-paper p-5">
          <div className="font-hand font-bold text-xl mb-3">Room Settings</div>
          {Object.entries(SETTING_OPTS).map(([k, cfg]) => (
            <div key={k} className="mb-3">
              <div className="font-semibold text-sm text-ink2 mb-1">{cfg.label}</div>
              <div className="flex flex-wrap gap-1.5">
                {cfg.opts.map((o) => (
                  <button key={String(o)} data-testid={`setting-${k}-${o}`} disabled={!isHost} onClick={() => setS(k, o)}
                    className="font-hand font-bold text-sm px-2.5 py-1" style={{ borderRadius: 8, border: "2px solid #1C1B1A", background: state.settings[k] === o ? "#FFDD2D" : "#FDFBF7", opacity: isHost ? 1 : 0.85 }}>
                    {o}{cfg.suffix || ""}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="flex gap-4 mt-4">
            {[["skipVote", "Skip Vote"], ["specialRoles", "Special Roles"]].map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-2 font-semibold text-sm cursor-pointer">
                <input type="checkbox" data-testid={`setting-${k}`} disabled={!isHost} checked={!!state.settings[k]} onChange={(e) => setS(k, e.target.checked)} />
                {lbl}
              </label>
            ))}
          </div>
          {!isHost && <div className="text-xs font-semibold text-ink2 mt-3">Only the host can change settings.</div>}
        </div>

        <div className="card-paper p-5">
          {isHost ? (
            <PaperButton data-testid="start-game" color="#C7EFC2" className="w-full text-2xl" onClick={start} disabled={n < state.minPlayers}>
              <Play size={22} strokeWidth={2.5} className="inline mr-2" />START GAME
            </PaperButton>
          ) : (
            <PaperButton data-testid="ready-btn" color={you?.ready ? "#C7EFC2" : "#FFE9A8"} className="w-full text-2xl" onClick={() => { audio.play("voteLock"); send({ type: "ready", value: !you?.ready }); }}>
              <Check size={22} strokeWidth={2.5} className="inline mr-2" />{you?.ready ? "READY!" : "READY"}
            </PaperButton>
          )}
          <div className="text-center font-semibold text-ink2 text-sm mt-2">{readyCount}/{n} ready</div>
        </div>
      </div>
    </div>
  );
}

/* ============================= PROMPT ============================= */
function PromptView({ state, you }) {
  const isImp = you?.role === "INK_IMPOSTER";
  const instruction = isImp
    ? "Blend in. Your prompt is a little different — draw something believable."
    : you?.role === "MISDIRECTOR"
      ? "Secret goal: get yourself eliminated. Draw suspiciously… but not too obvious."
      : "Find the players whose prompts don't match the rest.";
  return (
    <Centered>
      <div className="text-center max-w-2xl w-full animate-pop-in">
        <div className="font-hand font-bold text-2xl text-ink2 mb-2">YOUR SECRET PROMPT</div>
        <div className="card-paper p-8 relative" style={{ background: "#FFF7D6" }}>
          <div className="tape" style={{ top: -13, left: "42%", transform: "rotate(-4deg)" }} />
          <div className="font-hand font-bold text-3xl sm:text-4xl leading-tight">{you?.prompt || "…"}</div>
        </div>
        <div className="mt-5 flex flex-col items-center gap-3">
          {you?.roleLabel && <RoleBadge label={you.roleLabel} icon={you.roleIcon} color={you.roleColor} />}
          <p className="font-semibold text-ink2 max-w-md">{instruction}</p>
          <div className="font-hand font-bold text-lg">Drawing starts in <Timer endsAt={state.phaseEndsAt} className="inline text-2xl" warnSound={false} /></div>
        </div>
      </div>
    </Centered>
  );
}

/* ============================= DRAWING ============================= */
function DrawingView({ state, you, canvasRef, onStroke, onDone, submitted }) {
  if (!you?.alive) {
    return <SpectatorNote text="You've been eliminated. Watch the others draw…" endsAt={state.phaseEndsAt} />;
  }
  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="sticky-note bg-sticky-yellow px-4 py-2 font-hand font-bold text-lg" style={{ transform: "rotate(-1deg)" }}>{you?.prompt}</div>
        <div className="flex items-center gap-2">
          <span className="font-hand font-bold text-ink2">Time</span>
          <div className="card-flat px-4 py-1"><Timer endsAt={state.phaseEndsAt} className="text-3xl" /></div>
        </div>
      </div>
      <DrawingCanvas ref={canvasRef} onStroke={onStroke} disabled={submitted} />
      <div className="mt-4 flex justify-center">
        {submitted ? (
          <div className="font-hand font-bold text-xl text-role-artist flex items-center gap-2"><Check strokeWidth={3} /> Locked in! Waiting for others…</div>
        ) : (
          <PaperButton data-testid="done-drawing" color="#C7EFC2" className="text-xl px-8" onClick={onDone}>DONE</PaperButton>
        )}
      </div>
    </div>
  );
}

function SpectatorNote({ text, endsAt }) {
  return (
    <Centered>
      <div className="card-paper p-8 text-center max-w-md">
        <Skull size={40} strokeWidth={2.5} className="mx-auto mb-3 text-ink2" />
        <div className="font-hand font-bold text-2xl mb-2">Spectating</div>
        <p className="font-semibold text-ink2">{text}</p>
        {endsAt && <div className="mt-3"><Timer endsAt={endsAt} className="text-2xl" warnSound={false} /></div>}
      </div>
    </Centered>
  );
}

/* ============================= DRAWINGS GRID ============================= */
function DrawingGrid({ drawings, onPick, selected, disabled }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {drawings.map((d, i) => (
        <button
          key={d.label}
          data-testid={`drawing-${d.label}`}
          disabled={disabled}
          onClick={() => onPick && onPick(d.label)}
          className="card-paper p-2 relative animate-fade-up"
          style={{ animationDelay: `${i * 50}ms`, transform: `rotate(${(i % 3 - 1) * 1.5}deg)`, background: selected === d.label ? "#FFDD2D" : "#FDFBF7", cursor: onPick && !disabled ? "pointer" : "default" }}
        >
          <div className="tape" style={{ top: -11, left: "40%", width: 50, height: 20, transform: "rotate(-6deg)" }} />
          <div className="font-hand font-bold text-lg text-center mb-1">DRAWING {d.label}</div>
          <div className="bg-white card-flat overflow-hidden" style={{ aspectRatio: "640/480" }}>
            {d.image ? <img src={d.image} alt={`drawing ${d.label}`} className="w-full h-full object-contain" /> : <div className="w-full h-full flex items-center justify-center font-semibold text-ink2">no drawing</div>}
          </div>
          {selected === d.label && <div className="mt-1 font-hand font-bold text-center text-role-imposter">SUS!</div>}
        </button>
      ))}
    </div>
  );
}

function RevealView({ state }) {
  return (
    <div>
      <div className="text-center mb-5 animate-pop-in">
        <div className="font-hand font-bold text-3xl">The Drawings Are In!</div>
        <p className="font-semibold text-ink2">Anonymous — study them carefully…</p>
      </div>
      <DrawingGrid drawings={state.drawings || []} />
    </div>
  );
}

function VoteView({ state, you, send }) {
  const canVote = you?.alive;
  const pick = (label) => { if (!canVote) return; audio.play("voteSelect"); send({ type: "select_vote", label }); };
  return (
    <div>
      <div className="text-center mb-4">
        <div className="font-hand font-bold text-3xl sm:text-4xl">WHICH DRAWING LOOKS SUS?</div>
        <div className="flex items-center justify-center gap-4 mt-2">
          <span className="font-semibold text-ink2">{state.votedCount}/{state.aliveCount} voted</span>
          <div className="card-flat px-3 py-0.5"><Timer endsAt={state.phaseEndsAt} className="text-2xl" /></div>
        </div>
        {you?.vote && <div className="font-hand font-bold text-marker-blue mt-1">Your pick: {you.vote === "skip" ? "SKIP" : `Drawing ${you.vote}`} (tap to change)</div>}
      </div>
      {!canVote && <div className="text-center font-semibold text-ink2 mb-3">You're eliminated — you can't vote this round.</div>}
      <DrawingGrid drawings={state.drawings || []} onPick={pick} selected={you?.vote} disabled={!canVote} />
      {state.skipEnabled && canVote && (
        <div className="flex justify-center mt-5">
          <PaperButton data-testid="vote-skip" color={you?.vote === "skip" ? "#FFDD2D" : "#FDFBF7"} onClick={() => pick("skip")}>SKIP VOTE</PaperButton>
        </div>
      )}
    </div>
  );
}

function SkipView() {
  return (
    <Centered>
      <div className="card-paper p-10 text-center animate-pop-in">
        <div className="font-hand font-bold text-4xl mb-2">NO DRAWING SELECTED</div>
        <p className="font-semibold text-ink2">The room chose to skip. On to scoring…</p>
      </div>
    </Centered>
  );
}

/* ============================= HOT SEAT ============================= */
function HotSeatView({ state }) {
  const hs = state.hotseat;
  const [revealName, setRevealName] = useState(false);
  useEffect(() => { const t = setTimeout(() => setRevealName(true), 1600); return () => clearTimeout(t); }, []);
  return (
    <Centered>
      <div className="text-center w-full max-w-xl">
        <div className="font-hand font-bold text-5xl mb-3 animate-pop-in" style={{ color: "#E23B44" }}>🔥 HOT SEAT</div>
        <div className="card-paper p-3 mx-auto animate-pop-in" style={{ maxWidth: 460, background: "#FFF7D6" }}>
          <div className="bg-white card-flat overflow-hidden" style={{ aspectRatio: "640/480" }}>
            {hs?.image ? <img src={hs.image} alt="hot seat drawing" className="w-full h-full object-contain" /> : <div className="w-full h-full" />}
          </div>
        </div>
        <div className="mt-5 h-16 flex items-center justify-center">
          {!revealName ? (
            <div className="font-hand font-bold text-3xl text-ink2 animate-pulse">DRAWN BY…</div>
          ) : (
            <div className="flex items-center gap-3 animate-pop-in">
              <Avatar seed={hs?.avatar} size={52} />
              <div className="font-hand font-bold text-4xl">{hs?.name}</div>
            </div>
          )}
        </div>
      </div>
    </Centered>
  );
}

/* ============================= DEFENSE ============================= */
function DefenseView({ state, you, send, chat, reactions }) {
  const hs = state.hotseat;
  const mine = hs?.playerId === you?.id;
  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8">
        <div className="text-center mb-3">
          <div className="font-hand font-bold text-4xl">DEFEND YOUR DRAWING</div>
          <div className="flex items-center justify-center gap-3 mt-1">
            <div className="flex items-center gap-2"><Avatar seed={hs?.avatar} size={36} /><span className="font-hand font-bold text-xl">{hs?.name}</span></div>
            <div className="card-flat px-3 py-0.5"><Timer endsAt={state.phaseEndsAt} className="text-2xl" /></div>
          </div>
          {mine && <div className="font-hand font-bold text-role-imposter mt-1">It's you! Make your case (use voice chat too).</div>}
        </div>
        <div className="card-paper p-3 mx-auto" style={{ maxWidth: 460 }}>
          <div className="bg-white card-flat overflow-hidden" style={{ aspectRatio: "640/480" }}>
            {hs?.image && <img src={hs.image} alt="drawing" className="w-full h-full object-contain" />}
          </div>
        </div>
      </div>
      <div className="lg:col-span-4">
        <ChatPanel chat={chat} reactions={reactions} send={send} you={you} />
      </div>
    </div>
  );
}

/* ============================= FINAL VOTE ============================= */
function FinalVoteView({ state, you, send, chat, reactions }) {
  const hs = state.hotseat;
  const canVote = you?.alive;
  const vote = (v) => { audio.play("voteLock"); send({ type: "final_vote", value: v }); };
  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-8">
        <div className="text-center mb-3">
          <div className="font-hand font-bold text-4xl">FINAL DECISION</div>
          <div className="flex items-center justify-center gap-3 mt-1">
            <span className="font-semibold text-ink2">{state.finalVotedCount || 0} voted</span>
            <div className="card-flat px-3 py-0.5"><Timer endsAt={state.phaseEndsAt} className="text-2xl" /></div>
          </div>
        </div>
        <div className="card-paper p-3 mx-auto mb-4" style={{ maxWidth: 380 }}>
          <div className="flex items-center justify-center gap-2 mb-2"><Avatar seed={hs?.avatar} size={36} /><span className="font-hand font-bold text-xl">{hs?.name}</span></div>
          <div className="bg-white card-flat overflow-hidden" style={{ aspectRatio: "640/480" }}>
            {hs?.image && <img src={hs.image} alt="drawing" className="w-full h-full object-contain" />}
          </div>
        </div>
        {canVote ? (
          <div className="flex justify-center gap-4">
            <PaperButton data-testid="final-eliminate" color={you?.finalVote === "eliminate" ? "#E23B44" : "#FAC7D9"} className="text-xl px-8" onClick={() => vote("eliminate")}><Skull size={20} strokeWidth={2.5} className="inline mr-1" />ELIMINATE</PaperButton>
            <PaperButton data-testid="final-keep" color={you?.finalVote === "keep" ? "#3E9E42" : "#C7EFC2"} className="text-xl px-8" onClick={() => vote("keep")}><ShieldCheck size={20} strokeWidth={2.5} className="inline mr-1" />KEEP</PaperButton>
          </div>
        ) : <div className="text-center font-semibold text-ink2">You're eliminated — watching the vote.</div>}
        {you?.finalVote && <div className="text-center font-hand font-bold mt-2 text-marker-blue">You voted: {you.finalVote.toUpperCase()}</div>}
      </div>
      <div className="lg:col-span-4"><ChatPanel chat={chat} reactions={reactions} send={send} you={you} /></div>
    </div>
  );
}

/* ============================= ELIMINATION ============================= */
function EliminationView({ state }) {
  const r = state.result;
  if (!r) return null;
  if (r.survived) {
    return (
      <Centered>
        <div className="card-paper p-10 text-center animate-pop-in" style={{ background: "#C7EFC2" }}>
          <ShieldCheck size={56} strokeWidth={2.5} className="mx-auto mb-3 text-role-artist" />
          <div className="font-hand font-bold text-4xl">🛡️ {r.name} SURVIVES</div>
          <p className="font-semibold text-ink2 mt-2">The room decided to keep this drawing. No elimination.</p>
        </div>
      </Centered>
    );
  }
  return (
    <Centered>
      <div className="relative">
        <div className="absolute inset-0 -z-0 rounded-full animate-flood" style={{ background: r.roleColor, filter: "blur(40px)" }} />
        <div className="card-paper p-10 text-center animate-pop-in relative z-10" style={{ borderColor: r.roleColor }}>
          <DoorOpen size={56} strokeWidth={2.5} className="mx-auto mb-2" />
          <div className="font-hand font-bold text-4xl mb-3">🚪 {r.name} ELIMINATED</div>
          <div className="flex flex-col items-center gap-3">
            <Avatar seed={r.avatar} size={72} ring={r.roleColor} />
            <RoleBadge label={r.roleLabel} icon={r.roleIcon} color={r.roleColor} className="text-2xl px-5 py-2" />
            {r.misdirectorSuccess && <div className="font-hand font-bold text-2xl" style={{ color: "#8E24AA" }}>🟣 MISDIRECTOR SUCCESS!</div>}
          </div>
        </div>
      </div>
    </Centered>
  );
}

/* ============================= SCORING ============================= */
function ScoringView({ state, you }) {
  const summary = state.roundSummary || [];
  const myRank = summary.findIndex((p) => p.id === (you?.id || state.you?.id)) + 1;
  const me = summary.find((p) => p.id === (you?.id || state.you?.id));

  if (!me) {
    return (
      <Centered>
        <div className="card-paper p-8 text-center max-w-md">
          <div className="font-hand font-bold text-3xl mb-2">ROUND {state.round} RESULTS</div>
          <p className="font-semibold text-ink2">Waiting for score tally…</p>
        </div>
      </Centered>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-5 animate-pop-in">
        <div className="font-hand font-bold text-4xl">YOUR ROUND {state.round} RESULT</div>
        <p className="font-semibold text-ink2">Only you can see this breakdown</p>
      </div>

      <div
        className="card-paper p-6 animate-fade-up relative"
        style={{ background: "#FFF7D6", opacity: me.alive ? 1 : 0.85 }}
      >
        <div className="flex items-center gap-4 mb-4 pb-4 border-b-2 border-ink/10">
          <Avatar seed={me.avatar} size={56} />
          <div className="flex-1 min-w-0">
            <div className="font-hand font-bold text-2xl truncate flex items-center gap-2">
              {me.name}
              {!me.alive && <Skull size={18} className="text-ink2 inline" />}
            </div>
            {me.role && (
              <div
                className="text-xs font-bold px-2 py-0.5 inline-block mt-1"
                style={{
                  border: `2px solid ${me.roleColor || "#1C1B1A"}`,
                  color: me.roleColor || "#1C1B1A",
                  borderRadius: 8,
                }}
              >
                {me.role}
              </div>
            )}
          </div>
          {myRank > 0 && (
            <div className="text-right">
              <div className="text-xs font-bold text-ink2">RANK</div>
              <div className="font-hand font-bold text-2xl">#{myRank}</div>
            </div>
          )}
        </div>

        {/* Point Breakdown */}
        <div className="space-y-2 mb-5">
          <div className="font-hand font-bold text-lg text-ink2">Points This Round:</div>
          {me.deltas && me.deltas.length > 0 ? (
            <div className="space-y-1.5">
              {me.deltas.map((d, j) => (
                <div
                  key={j}
                  className="flex items-center justify-between sticky-note bg-sticky-green px-3 py-1.5 text-sm font-bold"
                >
                  <span>{d.reason}</span>
                  <span>+{d.points}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm font-semibold text-ink2 italic">No points earned this round.</div>
          )}
        </div>

        {/* Total Scores */}
        <div className="flex items-center justify-between pt-3 border-t-2 border-ink/10">
          <div>
            <div className="text-xs font-bold text-ink2">ROUND GAIN</div>
            <div className="font-hand font-bold text-2xl text-role-artist">
              {me.roundPoints > 0 ? `+${me.roundPoints}` : "0"}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-bold text-ink2">TOTAL SCORE</div>
            <div className="font-hand font-bold text-3xl">{me.total?.toLocaleString() || 0}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================= GAME OVER ============================= */
function GameOverView({ state, you, send, nav, playerId }) {
  const lb = state.leaderboard || [];
  const playerWin = state.teamResult === "PLAYER";
  return (
    <div className="max-w-3xl mx-auto relative">
      <Confetti />
      <div className="text-center mb-4 animate-pop-in">
        <div className="font-hand font-bold text-5xl">GAME OVER</div>
        <div className="mt-3 inline-block card-paper px-6 py-3" style={{ background: playerWin ? "#C7EFC2" : "#FAC7D9" }}>
          <div className="font-hand font-bold text-3xl">{playerWin ? "🟢 PLAYER TEAM WINS" : "🔴 IMPOSTER TEAM WINS"}</div>
        </div>
        {state.misdirectorSuccess && <div className="font-hand font-bold text-2xl mt-2" style={{ color: "#8E24AA" }}>🟣 A Misdirector achieved their secret goal!</div>}
      </div>

      {state.awards?.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
          {state.awards.map((a, i) => (
            <div key={i} className="sticky-note p-3 text-center" style={{ background: ["#FFE9A8", "#BAE3FB", "#FAC7D9", "#C7EFC2", "#FFCFA3"][i % 5], transform: `rotate(${(i % 2 ? 1 : -1) * 2}deg)` }}>
              <div className="text-2xl">{a.icon}</div>
              <div className="font-hand font-bold text-sm">{a.award}</div>
              <div className="font-semibold text-sm truncate">{a.name}</div>
            </div>
          ))}
        </div>
      )}

      <div className="font-hand font-bold text-2xl mb-2 flex items-center gap-2"><Trophy strokeWidth={2.5} /> Final Leaderboard</div>
      <div className="space-y-2">
        {lb.map((p, i) => (
          <div key={p.id} className="card-paper p-3 flex items-center gap-3 animate-fade-up" style={{ animationDelay: `${i * 60}ms`, background: i === 0 ? "#FFF7D6" : "#FDFBF7" }}>
            <div className="text-2xl w-8 text-center">{["🥇", "🥈", "🥉"][i] || `#${i + 1}`}</div>
            <Avatar seed={p.avatar} size={44} />
            <div className="flex-1 min-w-0">
              <div className="font-hand font-bold truncate">{p.name}{p.id === playerId && <span className="text-sm text-marker-blue"> (you)</span>}</div>
              <div className="text-sm font-bold" style={{ color: p.roleColor }}>{p.roleIcon} {p.role}</div>
            </div>
            <div className="font-hand font-bold text-2xl">{p.score.toLocaleString()}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap justify-center gap-3 mt-6">
        {you?.isHost && <PaperButton data-testid="play-again" color="#C7EFC2" className="text-xl" onClick={() => { audio.play("playAgain"); send({ type: "play_again" }); }}><RotateCcw size={20} strokeWidth={2.5} className="inline mr-1" />PLAY AGAIN</PaperButton>}
        <PaperButton data-testid="new-room" className="text-xl" onClick={() => { audio.play("click"); nav("/play?mode=create"); }}>NEW ROOM</PaperButton>
        <PaperButton data-testid="go-home" className="text-xl" onClick={() => { audio.play("click"); nav("/"); }}><HomeIcon size={18} strokeWidth={2.5} className="inline mr-1" />HOME</PaperButton>
      </div>
      {!you?.isHost && <div className="text-center font-semibold text-ink2 mt-3">Waiting for the host to start a new game…</div>}
    </div>
  );
}

/* ============================= CHAT + REACTIONS ============================= */
const REACTIONS = ["😂", "👀", "🤔", "💀", "🧠", "😱", "❌", "✅"];
function ChatPanel({ chat, reactions, send, you }) {
  const [text, setText] = useState("");
  const boxRef = useRef(null);
  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [chat]);
  const sendMsg = () => { const t = text.trim(); if (!t) return; send({ type: "chat", text: t }); setText(""); };
  return (
    <div className="card-paper p-3 flex flex-col h-[420px] relative">
      <div className="font-hand font-bold text-lg mb-2">Room Chat</div>
      <div ref={boxRef} className="flex-1 overflow-y-auto space-y-1.5 pr-1" data-testid="chat-box">
        {chat.map((m) => (
          <div key={m.id} className="text-sm">
            {m.system ? <span className="italic text-ink2 font-semibold">— {m.text}</span>
              : <span><b className="font-hand" style={{ color: m.pid === you?.id ? "#2D7DD2" : "#1C1B1A" }}>{m.name}:</b> <span className="font-semibold">{m.text}</span></span>}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-1 my-2">
        {REACTIONS.map((e) => <button key={e} data-testid={`react-${e}`} onClick={() => send({ type: "reaction", emoji: e })} className="text-lg hover:scale-125 transition-transform">{e}</button>)}
      </div>
      <div className="flex gap-2">
        <input data-testid="chat-input" value={text} maxLength={180} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder="Say something…" className="flex-1 card-flat px-3 py-2 font-semibold outline-none focus-mark" />
        <button data-testid="chat-send" onClick={sendMsg} className="btn-sketch p-2"><Send size={18} strokeWidth={2.5} /></button>
      </div>
      <div className="absolute inset-x-0 top-0 pointer-events-none overflow-hidden h-full">
        {reactions.map((r) => (
          <div key={r.rid} className="absolute text-3xl animate-pop-in" style={{ left: `${20 + Math.random() * 60}%`, bottom: 60, animation: "fade-up 2.5s ease-out forwards" }}>{r.emoji}</div>
        ))}
      </div>
    </div>
  );
}

function Confetti() {
  const bits = useMemo(() => Array.from({ length: 60 }).map((_, i) => ({
    id: i, left: Math.random() * 100, delay: Math.random() * 0.8, dur: 2 + Math.random() * 2,
    color: ["#FFDD2D", "#E23B44", "#2D7DD2", "#3E9E42", "#8E24AA", "#E86AA6"][i % 6], rot: Math.random() * 360,
  })), []);
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
      {bits.map((b) => (
        <div key={b.id} style={{ position: "absolute", left: `${b.left}%`, top: -20, width: 9, height: 14, background: b.color, transform: `rotate(${b.rot}deg)`, animation: `confetti-fall ${b.dur}s linear ${b.delay}s infinite` }} />
      ))}
      <style>{`@keyframes confetti-fall{0%{transform:translateY(-20px) rotate(0);opacity:1}100%{transform:translateY(100vh) rotate(540deg);opacity:0.9}}`}</style>
    </div>
  );
}